import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useIsMobile } from '@/shared/hooks/useIsMobile';

interface CursorPaginationProps {
    hasPrev: boolean;
    hasNext: boolean;
    onPrev: () => void;
    onNext: () => void;
    style?: React.CSSProperties;
}

const CursorPagination: React.FC<CursorPaginationProps> = ({ hasPrev, hasNext, onPrev, onNext, style }) => {
    const isMobile = useIsMobile();

    if (!hasPrev && !hasNext) return null;

    const buttonStyle = (disabled: boolean): React.CSSProperties => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? 'var(--space-sm)' : 'var(--space-md)',
        borderRadius: 'var(--radius-xs)',
        backgroundColor: disabled ? 'transparent' : 'var(--row-hover)',
        border: '1px solid var(--app-divider)',
        color: disabled ? 'var(--app-text-secondary)' : 'var(--app-text-primary)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s',
    });

    return (
        <nav
            aria-label="Pagination"
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? 'var(--space-sm)' : 'var(--space-lg)',
                padding: isMobile ? 'var(--space-xl) var(--space-lg)' : 'var(--space-2xl)',
                ...style,
            }}
        >
            <button
                type="button"
                onClick={onPrev}
                disabled={!hasPrev}
                style={buttonStyle(!hasPrev)}
                aria-label="Previous page"
                title="Previous page"
            >
                <ChevronLeft size={isMobile ? 14 : 16} />
            </button>

            <button
                type="button"
                onClick={onNext}
                disabled={!hasNext}
                style={buttonStyle(!hasNext)}
                aria-label="Next page"
                title="Next page"
            >
                <ChevronRight size={isMobile ? 14 : 16} />
            </button>
        </nav>
    );
};

export default CursorPagination;
